import { createSignal, onCleanup, onMount, Show } from 'solid-js';
import { Tooltip } from './Tooltip';
import { useToast } from './MessageBox';
import { useApi } from '../api';
import { useTranslation } from '../hooks/useTranslation';
import {
    armSetupMode,
    cancelSetupMode,
    registerSetupModeField,
    setupModeArmedField,
} from '../services/setupMode';
import { isFeatureEnabled } from '../services/featureFlags';
import type { SetupModeBlockPosDto, SetupModeValueDto } from '../api/generated/schemas';
import styles from './SetupModeButton.module.css';

const SETUP_MODE_FLAG = 'SETUP_MODE';
const ARM_ERROR_KEYS = ['no_fullcontrol_session', 'bot_not_allowed', 'client_too_old', 'already_armed'];

interface SetupModeButtonProps {
    /** Unique within the page; the captured value comes back addressed to it. */
    fieldId: string;
    kind: 'BlockPos' | 'ChestRef' | 'ChestSet' | 'Region' | 'LookAngles' | 'ItemStack';
    /** Shown in the in-game HUD, so it is already translated when it goes over the wire. */
    label: string;
    hint?: string;
    citybuild?: number | null;
    allowedBots: string[];
    /** Read at arm time, so a page holding a long chest list only fetches it when a capture starts. */
    currentValue?: () => SetupModeValueDto | undefined | Promise<SetupModeValueDto | undefined>;
    /** Where the bot stands when it uses this value, so the mod can warn about an unreachable pick. */
    anchor?: SetupModeBlockPosDto;
    reach?: number | null;
    disabled?: boolean;
    onCaptured: (value: SetupModeValueDto) => void;
}

export default function SetupModeButton(props: SetupModeButtonProps) {
    // Gated here rather than at each call site, so every picker on every page is covered at once and
    // a user without the flag never fires the poll that backs a capture.
    if (!isFeatureEnabled(SETUP_MODE_FLAG)) return null;

    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const [arming, setArming] = createSignal(false);

    const isMine = () => setupModeArmedField() === props.fieldId;
    const blockedByOther = () => setupModeArmedField() !== null && !isMine();

    onMount(() => {
        onCleanup(registerSetupModeField(props.fieldId, {
            onCaptured: (value) => props.onCaptured(value),
            onEnded: (reason) => {
                toast?.warning(reason === 'possession_ended'
                    ? t('SETUP_MODE_ENDED_POSSESSION')
                    : t('SETUP_MODE_ENDED_GENERIC'));
            },
        }, api));
    });

    async function arm() {
        setArming(true);
        try {
            const current = props.currentValue ? await props.currentValue() : undefined;
            const error = await armSetupMode({
                fieldId: props.fieldId,
                kind: props.kind,
                label: props.label,
                hint: props.hint ?? null,
                citybuild: props.citybuild ?? null,
                allowedBots: props.allowedBots,
                currentValue: current,
                anchor: props.anchor,
                reach: props.reach ?? null,
            });
            if (!error) {
                toast?.info(t('SETUP_MODE_ARMED', props.label));
                return;
            }
            // The backend answers in English; the key is what lets the operator read it in their own.
            const localised = ARM_ERROR_KEYS.includes(error.key) ? t(`SETUP_MODE_ERR_${error.key.toUpperCase()}`) : null;
            toast?.error(localised ?? error.message);
        } finally {
            setArming(false);
        }
    }

    async function cancel() {
        await cancelSetupMode();
        toast?.info(t('SETUP_MODE_CANCELLED'));
    }

    return (
        <span class={styles.wrap}>
            <Tooltip content={blockedByOther() ? t('SETUP_MODE_BUSY') : t('SETUP_MODE_TOOLTIP')}>
                <button
                    type="button"
                    class={`${styles.trigger} ${isMine() ? styles.armed : ''}`}
                    disabled={props.disabled || arming() || blockedByOther()}
                    aria-label={t('SETUP_MODE_TOOLTIP')}
                    onClick={() => void arm()}
                >
                    <i class={`fa-solid ${arming() || isMine() ? 'fa-spinner fa-spin' : 'fa-location-crosshairs'}`} />
                </button>
            </Tooltip>
            <Show when={isMine()}>
                <span class={styles.waiting}>{t('SETUP_MODE_WAITING')}</span>
                <button type="button" class={styles.cancel} onClick={() => void cancel()}>
                    {t('SETUP_MODE_CANCEL')}
                </button>
            </Show>
        </span>
    );
}
